import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminSearchField,
  AdminListFilterDropdown,
  AdminTableHeader,
  AdminTableBody,
  AdminSupportingText,
  AdminHeaderAction,
  AdminRowActionsButton,
  AdminRowMenuItem,
  ADMIN_INLINE_ROW_ACTIONS_MENU_CLASS,
  AdminRowMenuIcon,
  ADMIN_TOOLBAR_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import useClickOutside from '../../../Hooks/useClickOutside';
import { CONFIG_LIST_PAGE_SIZE } from '../../../utils/configurationTableUx';

const ARTICLE_USE_OPTIONS = ['Answer Guide', 'Troubleshooting', 'Support Process'];
const CATEGORY_OPTIONS = [
  'Account Access',
  'Billing & Subscriptions',
  'Technical Help',
  'General Support',
  'Appeal',
  'Compliance Query',
];

const gridTemplateColumns = 'minmax(110px, 1fr) minmax(320px, 3.2fr) minmax(180px, 1.5fr) minmax(170px, 1.4fr) minmax(52px, 52px)';

const buildDummyArticles = () => [
  {
    articleId: 'KB-0001',
    title: 'Password reset email not received',
    category: 'Account Access',
    articleUse: 'Troubleshooting',
    quickAnswer:
      'Confirm account email, ask customer to check spam and junk, then verify whether reset email delivery is operating normally.',
  },
  {
    articleId: 'KB-0002',
    title: 'How business subscriptions are billed',
    category: 'Billing & Subscriptions',
    articleUse: 'Answer Guide',
    quickAnswer: 'Subscriptions are billed in recurring cycles with invoice records available in billing history.',
  },
  {
    articleId: 'KB-0003',
    title: 'When to route a compliance query to the assigned team',
    category: 'Compliance Query',
    articleUse: 'Support Process',
    quickAnswer: 'Route once required evidence is confirmed and issue falls into compliance escalation criteria.',
  },
  {
    articleId: 'KB-0004',
    title: 'Reply guide for removed review appeal requests',
    category: 'Appeal',
    articleUse: 'Answer Guide',
    quickAnswer: 'Use approved wording, collect appeal ID, then confirm expected review timeline.',
  },
  {
    articleId: 'KB-0005',
    title: 'Unable to submit website form',
    category: 'Technical Help',
    articleUse: 'Troubleshooting',
    quickAnswer: 'Check required fields, browser console/network issues, then capture replication steps.',
  },
  {
    articleId: 'KB-0006',
    title: 'Handling duplicate charge enquiries',
    category: 'Billing & Subscriptions',
    articleUse: 'Support Process',
    quickAnswer: 'Validate duplicate transaction evidence, trigger refund flow, and notify customer of settlement window.',
  },
];

const KnowledgeBase = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const openArticleView = (article) => {
    navigate(`/admin/support/kb/${article.articleId}?mode=view`, {
      state: { from: location.pathname },
    });
  };
  const [articles] = useState(buildDummyArticles);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ category: null, articleUse: null });
  const [currentPage, setCurrentPage] = useState(1);
  const [kbRowActionsOpen, setKbRowActionsOpen] = useState({});
  const kbRowActionsRefs = useRef({});

  const toggleKbRowActions = (rowKey) => {
    setKbRowActionsOpen((prev) => {
      if (prev[rowKey]) return {};
      return { [rowKey]: true };
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideAll = Object.values(kbRowActionsRefs.current).every(
        (ref) => ref && !ref.contains(event.target)
      );
      if (isOutsideAll) setKbRowActionsOpen({});
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const categoryRef = useRef(null);
  const articleUseRef = useRef(null);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [articleUseOpen, setArticleUseOpen] = useState(false);

  useClickOutside(categoryRef, () => setCategoryOpen(false));
  useClickOutside(articleUseRef, () => setArticleUseOpen(false));

  const filteredArticles = useMemo(() => {
    const lowerSearch = search.trim().toLowerCase();

    return articles.filter((article) => {
      const matchesCategory = !filters.category || article.category === filters.category;
      const matchesArticleUse = !filters.articleUse || article.articleUse === filters.articleUse;
      const matchesSearch =
        !lowerSearch ||
        article.title.toLowerCase().includes(lowerSearch) ||
        article.articleId.toLowerCase().includes(lowerSearch) ||
        article.category.toLowerCase().includes(lowerSearch) ||
        article.articleUse.toLowerCase().includes(lowerSearch) ||
        article.quickAnswer.toLowerCase().includes(lowerSearch);

      return matchesCategory && matchesArticleUse && matchesSearch;
    });
  }, [articles, filters, search]);

  const totalCount = filteredArticles.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / CONFIG_LIST_PAGE_SIZE));
  const effectivePage = Math.min(currentPage, totalPages);

  useEffect(() => {
    setCurrentPage((p) => Math.min(p, Math.max(1, Math.ceil(filteredArticles.length / CONFIG_LIST_PAGE_SIZE))));
  }, [filteredArticles.length]);

  const paginatedArticles = useMemo(() => {
    const start = (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE;
    return filteredArticles.slice(start, start + CONFIG_LIST_PAGE_SIZE);
  }, [filteredArticles, effectivePage]);

  const rangeStart = totalCount === 0 ? 0 : (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE + 1;
  const rangeEnd = Math.min(effectivePage * CONFIG_LIST_PAGE_SIZE, totalCount);
  const showPagination = totalCount > CONFIG_LIST_PAGE_SIZE;

  return (
    <AdminLayout>
      <div>
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1">
            <AdminPageTitle>Knowledge Base</AdminPageTitle>
          </div>
          <AdminHeaderAction onClick={() => navigate('/admin/support/kb/new')}>
            + Add Article
          </AdminHeaderAction>
        </div>

        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center lg:justify-between">
            <div className="w-full max-w-[450px]">
              <AdminSearchField
                placeholder="Search by title, keyword, or topic..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <div className="flex flex-wrap gap-3 items-center w-full lg:w-auto lg:justify-end">
              <AdminListFilterDropdown
                label="Category"
                value={filters.category}
                options={[
                  { value: null, label: 'All Categories' },
                  ...CATEGORY_OPTIONS.map((category) => ({ value: category, label: category })),
                ]}
                onChange={(value) => {
                  setFilters((prev) => ({ ...prev, category: value }));
                  setCurrentPage(1);
                }}
                menuRef={categoryRef}
                open={categoryOpen}
                setOpen={setCategoryOpen}
              />
              <AdminListFilterDropdown
                label="Article Use"
                value={filters.articleUse}
                options={[
                  { value: null, label: 'All Article Uses' },
                  ...ARTICLE_USE_OPTIONS.map((use) => ({ value: use, label: use })),
                ]}
                onChange={(value) => {
                  setFilters((prev) => ({ ...prev, articleUse: value }));
                  setCurrentPage(1);
                }}
                menuRef={articleUseRef}
                open={articleUseOpen}
                setOpen={setArticleUseOpen}
              />
            </div>
          </div>
        </div>

        <DashboardMainContainer className={ADMIN_TABLE_CLASS}>
          {paginatedArticles.length === 0 ? (
            <div className="text-center py-10">
              <AdminSupportingText className="text-mediumgray">No articles match your filters.</AdminSupportingText>
            </div>
          ) : (
            <>
              <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns }}>
                <AdminTableHeader>Article ID</AdminTableHeader>
                <AdminTableHeader>Title</AdminTableHeader>
                <AdminTableHeader>Category</AdminTableHeader>
                <AdminTableHeader>Article Use</AdminTableHeader>
                <AdminTableHeader className="text-center">Actions</AdminTableHeader>
              </div>
              {paginatedArticles.map((article) => (
                <div
                  key={article.articleId}
                  className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4 cursor-pointer"
                  style={{ gridTemplateColumns }}
                  onClick={() => openArticleView(article)}
                >
                  <div className="flex items-center">
                    <AdminTableBody>{article.articleId}</AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminTableBody className="truncate" title={article.title}>
                      {article.title}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center">
                    <AdminTableBody>{article.category}</AdminTableBody>
                  </div>
                  <div className="flex items-center">
                    <AdminTableBody>{article.articleUse}</AdminTableBody>
                  </div>
                  <div className="flex items-center justify-center relative" onClick={(e) => e.stopPropagation()}>
                    <div
                      className="relative inline-flex"
                      ref={(el) => {
                        kbRowActionsRefs.current[article.articleId] = el;
                      }}
                    >
                      <AdminRowActionsButton
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleKbRowActions(article.articleId);
                        }}
                        title="Actions"
                        aria-label={`Open actions for ${article.articleId}`}
                      >
                        <AdminRowMenuIcon />
                      </AdminRowActionsButton>
                      {kbRowActionsOpen[article.articleId] && (
                        <div className={ADMIN_INLINE_ROW_ACTIONS_MENU_CLASS}>
                          <AdminRowMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              openArticleView(article);
                              setKbRowActionsOpen({});
                            }}
                          >
                            View
                          </AdminRowMenuItem>
                          <AdminRowMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/admin/support/kb/${article.articleId}?mode=edit`, {
                                state: { from: location.pathname },
                              });
                              setKbRowActionsOpen({});
                            }}
                          >
                            Edit
                          </AdminRowMenuItem>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}

        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalCount}
          currentPage={effectivePage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>
    </AdminLayout>
  );
};

export default KnowledgeBase;
